/**
 * CR500 Phase 2A: RibbonBlock
 * CR312: POINTS V2 parity - purple ribbon from DEAL_TYPE_REGISTRY
 * CR314: IN_KIND V2 parity - amber ribbon with "FREE" label
 * CR325: Refactored to use components.ribbon as single source of truth
 * CR326: All deal types (including POINTS) are config-driven:
 *        - Amount toggle controls numeric display
 *        - Deal Type Label toggle controls label (default/custom)
 *        - Composition: "{amount} {label}" based on enabled toggles
 * CR308: Visibility is gated EXCLUSIVELY by blockToggles.ribbon (block.enabled).
 *        - components.ribbon is STYLING/CONFIG ONLY, never visibility.
 *        - Legacy showRibbon fields are IGNORED for V2 rendering.
 * CR310: Added support for Asset Studio ribbon graphics
 *        - graphic_source: "system" | "asset"
 *        - asset_id: UUID of Asset Studio asset
 * 
 * Renders the corner ribbon for deal cards.
 * 
 * CR325: Now reads from resolvedSpec.resolved.components.ribbon when available.
 * When components.ribbon exists, legacy styling.* fields are ignored for v2 rendering.
 */

import React from "react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal, DealCategory } from "@/lib/types/deals";
import { cn } from "@/lib/utils";
import { getDealType } from "@/lib/deals/getDealType";
import { getDealRibbonBg } from "@/lib/deals/dealTypeRegistry";
import { 
  buildRibbonViewModel, 
  type RibbonComponentConfig 
} from "@/lib/deal-cards/ribbonViewModel";
import { useRibbonAsset } from "@/hooks/useRibbonAsset";
import { 
  resolveRibbonColor, 
  type RibbonColorConfig,
} from "@/lib/deal-cards/resolveRibbonColor";
import { useGlobalTheme } from "@/providers/GlobalThemeProvider";

// CR500: Static ribbon theme class map - prevents dynamic Tailwind issues
const RIBBON_THEME_CLASS: Record<string, string> = {
  emerald: "bg-emerald-600",
  purple: "bg-purple-600",
  amber: "bg-amber-600",
  slate: "bg-slate-600",
  blue: "bg-blue-600",
  green: "bg-green-600",
  orange: "bg-orange-500",
  gray: "bg-gray-500",
  red: "bg-red-600",
  teal: "bg-teal-600",
  navy: "bg-slate-800",
};

// Deal type to ribbon color mapping
const DEAL_TYPE_RIBBON_COLORS: Record<DealCategory, string> = {
  BONUS: "bg-emerald-600",
  DISCOUNT: "bg-orange-500",
  POINTS: "bg-purple-600",
  IN_KIND: "bg-amber-600",
  UNCLASSIFIED: "bg-slate-600",
};

interface RibbonBlockProps extends DealCardBlockProps<Deal> {
  /** Ribbon background class override (from registry) */
  ribbonBgOverride?: string;
  /** Ribbon theme key (from registry) */
  ribbonTheme?: string;
  /** Ribbon text override (from registry) */
  ribbonTextOverride?: string;
  /** Default ribbon class (from getRibbonClass) */
  defaultRibbonClass?: string;
}

export const RibbonBlock: React.FC<RibbonBlockProps> = ({
  deal,
  block,
  context,
  ribbonBgOverride,
  ribbonTheme,
}) => {
  // CR308: SSOT GATE - Visibility is EXCLUSIVELY controlled by block.enabled
  // which comes from surfaceOverrides.primary.blockToggles.ribbon
  // DO NOT add any other visibility checks (no components.ribbon presence, no showRibbon)
  if (!block.enabled) {
    return null;
  }

  const { category: dealCategory } = getDealType(deal);
  
  // CR308/CR325: components.ribbon is for STYLING ONLY (colors, gradients, text config)
  // Its presence/absence does NOT affect visibility - only block.enabled does
  const componentsRibbon = (context as any)?.components?.ribbon as (RibbonComponentConfig & {
    styling?: {
      baseColorHex?: string | null;
      borderColorHex?: string | null;
      gradientFrom?: string;
      gradientTo?: string;
      borderColor?: string;
    };
    /** CR310: Graphic source */
    graphic_source?: "system" | "asset";
    /** CR310: Asset ID for ribbon graphic */
    asset_id?: string | null;
    /** CR337: Color source */
    color_source?: "custom_hex" | "theme_token" | "asset_default";
    /** CR337: Custom HEX color */
    color_hex?: string | null;
  }) | undefined;
  const ribbonConfig = componentsRibbon ?? (context.styleOverrides as any)?.ribbon as RibbonComponentConfig | undefined;
  
  // CR310: Get graphic source and asset ID
  const graphicSource = componentsRibbon?.graphic_source ?? "system";
  const assetId = componentsRibbon?.asset_id;
  
  // CR310: Fetch ribbon asset if needed
  const { asset: ribbonAsset, imageSrc: ribbonAssetSrc, isMissing: assetMissing } = useRibbonAsset(
    graphicSource === "asset" ? assetId : null
  );
  
  // CR310: Use asset-based rendering if configured and asset exists
  const useAssetRendering = graphicSource === "asset" && ribbonAssetSrc && !assetMissing;
  
  // Build view model using shared helper
  const viewModel = buildRibbonViewModel(deal, dealCategory, ribbonConfig);
  
  // CR337/CR339: Resolve ribbon fill color using the priority chain with Theme Labs tokens
  const { tokens: globalTokens } = useGlobalTheme();
  const colorConfig: RibbonColorConfig | undefined = componentsRibbon?.color_source
    ? { color_source: componentsRibbon.color_source, color_hex: componentsRibbon.color_hex }
    : undefined;
  const assetDefaultTint = ribbonAsset?.default_tint ?? null;
  const resolvedColor = resolveRibbonColor(colorConfig, dealCategory, assetDefaultTint, globalTokens.dealTypeThemes);
  
  // CR401: Extract HEX-based styling from components.ribbon.styling (legacy gradient system)
  const ribbonStyling = componentsRibbon?.styling;
  const hasHexStyling = ribbonStyling?.baseColorHex != null;
  
  // CR337: Build background style from resolved color chain
  // Priority: CR337 color_source > legacy HEX styling > theme class
  const hasResolvedCssColor = resolvedColor.cssColor != null;
  
  const resolvedRibbonClass = (hasResolvedCssColor || hasHexStyling || useAssetRendering)
    ? "" // Don't use Tailwind class when using inline color
    : resolvedColor.tailwindClass
      || ribbonBgOverride 
      || (ribbonTheme ? (RIBBON_THEME_CLASS[ribbonTheme] ?? getDealRibbonBg(dealCategory)) : null)
      || DEAL_TYPE_RIBBON_COLORS[dealCategory]
      || getDealRibbonBg(dealCategory);
  
  // CR337/CR401: Build inline style - CR337 takes priority over legacy
  const ribbonInlineStyle: React.CSSProperties = hasResolvedCssColor
    ? { backgroundColor: resolvedColor.cssColor! }
    : hasHexStyling
      ? {
          background: ribbonStyling.gradientFrom && ribbonStyling.gradientTo
            ? `linear-gradient(135deg, hsl(${ribbonStyling.gradientFrom}), hsl(${ribbonStyling.gradientTo}))`
            : `hsl(${ribbonStyling.baseColorHex})`,
          borderColor: ribbonStyling.borderColor
            ? `hsla(${ribbonStyling.borderColor.replace(' / ', ', ')})`
            : undefined,
          borderWidth: ribbonStyling.borderColor ? '2px' : undefined,
          borderStyle: ribbonStyling.borderColor ? 'solid' : undefined,
        }
      : {};
  
  const containerClass = cn(
    "absolute top-0 right-0 z-10 h-32 w-36",
    !useAssetRendering && resolvedRibbonClass
  );

  const surfaceStyle: React.CSSProperties = {
    ...ribbonInlineStyle,
  };

  // Build typography styles from config
  const typographyStyle: React.CSSProperties = {
    fontFamily: viewModel.typography?.fontFamily !== "default" && viewModel.typography?.fontFamily !== "inherit"
      ? viewModel.typography?.fontFamily
      : undefined,
    fontSize: viewModel.typography?.fontSize ? `${viewModel.typography.fontSize}px` : undefined,
    fontWeight: viewModel.typography?.fontWeight,
  };

  // CR310: Render with asset or CSS-based background
  return (
    <div 
      data-testid="deal-ribbon"
      data-block="ribbon"
      data-block-id={block.id}
      data-component-key="ribbon"
      data-ribbon-source={componentsRibbon ? "components" : "legacy"}
      data-ribbon-hex={resolvedColor.cssColor ?? (hasHexStyling ? ribbonStyling?.baseColorHex : undefined)}
      data-ribbon-color-source={resolvedColor.source}
      data-ribbon-graphic={useAssetRendering ? "asset" : "system"}
      className={containerClass}
      style={useAssetRendering ? {} : undefined}
    >
      {useAssetRendering ? (
        <img
          src={ribbonAssetSrc}
          alt=""
          className="absolute inset-0 h-full w-full object-cover pointer-events-none"
          aria-hidden="true"
        />
      ) : (
        <>
          <div
            aria-hidden="true"
            className="absolute right-0 top-0 h-10 w-36 rounded-bl-xl rounded-tr-[calc(var(--wi-card-radius,24px)-1px)]"
            style={surfaceStyle}
          />
          <div
            aria-hidden="true"
            className="absolute right-0 top-8 h-24 w-28"
            style={{ ...surfaceStyle, clipPath: "polygon(100% 0, 100% 100%, 0 0)" }}
          />
          <div
            aria-hidden="true"
            className="absolute right-0 top-8 h-3 w-16 rounded-bl-xl"
            style={{ ...surfaceStyle, opacity: 0.85 }}
          />
        </>
      )}
      
      {/* Text content overlay */}
      <div className="absolute right-3 top-2.5 text-right text-white" style={typographyStyle}>
        {/* Primary line - CR326: All deal types use config-driven composition */}
        <span className="block text-[11px] uppercase tracking-[0.04em] opacity-95">
          {viewModel.primaryText}
        </span>
        
        {/* CR326: PointsSubline removed - POINTS now uses same config-driven logic as other types */}
        
        {/* Additional context for DISCOUNT only */}
        {dealCategory === "DISCOUNT" && deal.discount_percent && (
          <DiscountSubline deal={deal} />
        )}
        
        {/* Merchant subline (if enabled) */}
        {viewModel.showSubline && viewModel.merchantName && (
          <MerchantSubline 
            name={viewModel.merchantName}
            showLogo={ribbonConfig?.merchantSubline?.showLogo ?? true}
            showName={ribbonConfig?.merchantSubline?.showName ?? true}
            typography={viewModel.merchantTypography ?? viewModel.typography}
          />
        )}
      </div>
    </div>
  );
};

// ================================================================
// SUBCOMPONENTS
// ================================================================

// CR326: PointsSubline removed - rate qualifier now included in primaryText
// to prevent duplicate "per $X" display

function DiscountSubline({ deal }: { deal: Deal }) {
  const rawPurchaseBrand = deal.purchase_gift_card_brand ?? deal.gift_card_brand;
  
  if (rawPurchaseBrand && rawPurchaseBrand !== deal.merchant) {
    return (
      <span className="block max-w-[60px] truncate text-[9px] font-medium opacity-90">
        {rawPurchaseBrand} GC
      </span>
    );
  }
  
  return null;
}

interface MerchantSublineProps {
  name: string;
  showLogo: boolean;
  showName: boolean;
  typography?: {
    fontFamily?: string;
    fontSize?: number;
    fontWeight?: number;
  };
}

function MerchantSubline({ name, showLogo, showName, typography }: MerchantSublineProps) {
  const style: React.CSSProperties = {
    fontFamily: typography?.fontFamily !== "default" && typography?.fontFamily !== "inherit"
      ? typography?.fontFamily
      : undefined,
    fontSize: typography?.fontSize ? `${typography.fontSize}px` : "10px",
    fontWeight: typography?.fontWeight,
  };
  
  return (
    <div className="flex items-center gap-1 mt-0.5 opacity-80" style={style}>
      {showLogo && (
        <div className="w-3 h-3 bg-white/20 rounded-sm" />
      )}
      {showName && (
        <span className="truncate max-w-[60px]">{name}</span>
      )}
    </div>
  );
}

export default RibbonBlock;
